import React from "react";
import ReactDOM from "react-dom";
import {FaTimes} from "react-icons/fa";
import {sanitizeHtml} from "./sanitize";

interface ModalProps {
    visible: boolean;
    title: string;
    message?: string;
    onClose: () => void;
    buttonLabel?: string;
    imageUrl?: string;
    /** When provided a second "Confirm" button is shown alongside the cancel button. */
    onConfirm?: () => void;
    /** Label for the confirm button. Defaults to "Confirm". */
    confirmLabel?: string;
    /** Whether the confirm button should be disabled. */
    confirmDisabled?: boolean;
    /** Optional rich JSX body rendered instead of (or alongside) the message string. */
    children?: React.ReactNode;
    /** Override the default 580 px max-width of the modal card. */
    maxWidth?: string | number;
    /** Optional class for page-specific modal presentation. */
    className?: string;
    /** When true the footer button row is not rendered. */
    hideFooter?: boolean;
}

const CustomModal: React.FC<ModalProps> = ({
    visible, title, message, onClose,
    buttonLabel = "Close", imageUrl,
    onConfirm, confirmLabel = "Confirm",
    confirmDisabled = false,
    children, maxWidth, className, hideFooter = false,
}) => {
    if (!visible) return null;

    const contentStyle: React.CSSProperties = maxWidth
        ? {maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth}
        : {};

    const modal = (
        <div className="custom-toast-wrapper">
            <div className={`custom-toast-content${className ? ` ${className}` : ""}`} style={contentStyle}>
                <div className="custom-toast-header">
                    {imageUrl && (
                        <img src={imageUrl} alt="Modal image" style={{height: 32, width: 32, marginRight: 8}}/>
                    )}
                    <div className="custom-toast-title">
                        {title}
                    </div>
                    <button className="custom-toast-close" onClick={onClose}>
                        <FaTimes/>
                    </button>
                </div>

                <div className="custom-toast-body">
                    {children ? (
                        children
                    ) : (
                        <p className="text-lead" dangerouslySetInnerHTML={{__html: sanitizeHtml(message ?? "")}}/>
                    )}
                </div>

                <div className="custom-toast-footer">
                    {!hideFooter && (
                        <>
                            <button className="btn btn-cancel" onClick={onClose}>{buttonLabel}</button>
                            {onConfirm && (
                                <button
                                    className="btn btn-confirm"
                                    onClick={onConfirm}
                                    disabled={confirmDisabled}
                                >
                                    {confirmLabel}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default CustomModal;
